import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { SearchCoachByName } from "../../../services/CoachService";
import { useAxiosInstance } from "../../../services/Interceptor";
import {
  coachesFetchSuccess,
  coachLoading,
  coachError,
} from "../../../store/slices/coachSlice";
import DashboardCoachSearchBar from "./DashboardCoachSearchBar";
import DashboardCoachSearchList from "./DashboardCoachSearchList";

const ITEMS_PER_PAGE = 20;

function Coaches() {
  const dispatch = useDispatch();
  const apiInstance = useAxiosInstance();
  const r_coaches = useSelector((state) => state.coaches);
  const { coaches, loading, error } = r_coaches;

  const [searchTerm, setSearchTerm] = useState("");
  const [allFilteredCoaches, setAllFilteredCoaches] = useState([]);
  const [displayedCoaches, setDisplayedCoaches] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const observerTarget = useRef(null);

  const loadMoreCoaches = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);

    const nextPage = currentPage + 1;
    const startIndex = nextPage * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;

    const nextBatch = allFilteredCoaches.slice(startIndex, endIndex);

    if (nextBatch.length > 0) {
      setDisplayedCoaches((prev) => [...prev, ...nextBatch]);
      setCurrentPage(nextPage);
      setHasMore(endIndex < allFilteredCoaches.length);
    } else {
      setHasMore(false);
    }

    setIsLoadingMore(false);
  }, [isLoadingMore, hasMore, currentPage, allFilteredCoaches]);

  const resetPagination = useCallback((coachesData) => {
    const firstPage = coachesData.slice(0, ITEMS_PER_PAGE);
    setAllFilteredCoaches(coachesData);
    setDisplayedCoaches(firstPage);
    setCurrentPage(0);
    setHasMore(coachesData.length > ITEMS_PER_PAGE);
  }, []);

  const fetchCoaches = async () => {
    dispatch(coachLoading(true));
    try {
      const response = await SearchCoachByName(apiInstance, "");
      dispatch(coachesFetchSuccess(response));
    } catch (err) {
      dispatch(coachError(err.message || "An error occurred"));
    } finally {
      dispatch(coachLoading(false));
    }
  };

  useEffect(() => {
    fetchCoaches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const normalized = searchTerm.trim().toLowerCase();
    if (normalized === "") {
      resetPagination(coaches);
    } else {
      const result = coaches.filter((coach) => {
        const name = coach.name?.toLowerCase() || "";
        const lastname = coach.lastname?.toLowerCase() || "";
        const fullName = `${name} ${lastname}`;
        const fullLastName = `${lastname} ${name}`;
        return (
          name.includes(normalized) ||
          lastname.includes(normalized) ||
          fullName.includes(normalized) ||
          fullLastName.includes(normalized)
        );
      });
      resetPagination(result);
    }
  }, [searchTerm, coaches, resetPagination]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoadingMore &&
          !loading
        ) {
          loadMoreCoaches();
        }
      },
      { threshold: 0.1 },
    );

    const currentTarget = observerTarget.current;

    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, isLoadingMore, loading, loadMoreCoaches]);

  const handleSearch = (query) => {
    setSearchTerm(query);
  };

  return (
    <div className="flex flex-col p-4 pb-0 rounded-md bg-white lg:h-full">
      <h1 className="text-xl mb-4 uppercase">COACHES</h1>
      <DashboardCoachSearchBar onSearch={handleSearch} />
      <DashboardCoachSearchList
        loading={loading}
        error={error}
        displayedCoaches={displayedCoaches}
        observerTarget={observerTarget}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
      />
    </div>
  );
}

export default Coaches;
